import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { api } from '../lib/api'
import { klassenbuchApi } from '../lib/klassenbuchApi'
import { formatCell, formatPeriodLabel, type PeriodRow, type ViewerEntry } from '../lib/timetableViewerDisplay'
import { computeTimetableHighlights, untisWeekdayFromDate } from '../lib/timetableNowHighlight'
import {
  EmptyState,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent
} from './ui'

type DashboardTimetablePayload = {
  timetable: {
    id: number
    label: string
    status: string
    version_no: number
    valid_from: string
    valid_until: string
    school_year_name?: string
    location_name?: string | null
  } | null
  periods: PeriodRow[]
  weekdays_in_use: number[]
  weekday_labels: Record<number, string>
  entries: ViewerEntry[]
  target_user: {
    id: number
    username: string
    first_name: string
    last_name: string
    display_name: string
  }
  can_switch_user: boolean
}

type TeacherRow = {
  id: number
  username: string
  first_name?: string | null
  last_name?: string | null
  teacher_display_name?: string | null
}

function getTeacherLabel(t: TeacherRow): string {
  if (t.teacher_display_name && String(t.teacher_display_name).trim()) return String(t.teacher_display_name).trim()
  const n = [t.first_name, t.last_name].filter(Boolean).join(' ').trim()
  return n || t.username || `User ${t.id}`
}

function getLessonHighlightType(
  wd: number,
  periodNo: number,
  todayWd: number,
  todayInGrid: boolean,
  currentWeekday: number | null,
  currentPeriodNo: number | null,
  nextWeekday: number | null,
  nextPeriodNo: number | null
): 'current' | 'next' | null {
  if (
    todayInGrid &&
    wd === todayWd &&
    currentWeekday === todayWd &&
    currentPeriodNo != null &&
    periodNo === currentPeriodNo
  ) {
    return 'current'
  }
  if (nextWeekday != null && nextPeriodNo != null && wd === nextWeekday && periodNo === nextPeriodNo) {
    return 'next'
  }
  return null
}

export function DashboardTimetableWidget({
  allowUserPicker,
  authUserId
}: {
  allowUserPicker: boolean
  authUserId?: number | null
}) {
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('')

  useEffect(() => {
    setSelectedUserId('')
  }, [authUserId])

  const { data: teachersRes } = useQuery(
    ['dashboard', 'timetable-teachers'],
    async () => {
      const res = await api.get('/appointments/global/teachers')
      return (res.data?.data || []) as TeacherRow[]
    },
    { enabled: allowUserPicker, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const effectiveUserId =
    allowUserPicker && selectedUserId !== '' && Number.isFinite(Number(selectedUserId))
      ? Number(selectedUserId)
      : undefined

  const { data, isLoading, isError, error } = useQuery(
    ['timetables', 'dashboard-widget', effectiveUserId ?? 'self'],
    async () => {
      const params =
        effectiveUserId != null && effectiveUserId > 0 ? { user_id: String(effectiveUserId) } : undefined
      const res = await api.get('/timetables/dashboard-widget', { params })
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Stundenplan konnte nicht geladen werden')
      }
      return res.data.data as DashboardTimetablePayload
    },
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  )

  const [subjectColorById, setSubjectColorById] = useState<Map<number, string>>(() => new Map())
  const [nowTick, setNowTick] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    klassenbuchApi
      .getSubjectColors()
      .then((colorsPayload) => {
        if (cancelled) return
        const m = new Map<number, string>()
        for (const [k, v] of Object.entries(colorsPayload.colors || {})) {
          const id = Number(k)
          if (Number.isFinite(id) && id > 0 && typeof v === 'string' && v) m.set(id, v)
        }
        setSubjectColorById(m)
      })
      .catch(() => {
        if (!cancelled) setSubjectColorById(new Map())
      })
    return () => {
      cancelled = true
    }
  }, [])

  const cellMap = useMemo(() => {
    const m = new Map<string, ViewerEntry[]>()
    ;(data?.entries || []).forEach((e) => {
      const k = `${e.weekday}:${e.period_no}`
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(e)
    })
    return m
  }, [data?.entries])

  const weekdays = useMemo(
    () => (data?.weekdays_in_use?.length ? data.weekdays_in_use : [1, 2, 3, 4, 5]),
    [data?.weekdays_in_use]
  )

  const periodRows = useMemo((): PeriodRow[] => {
    if (data?.periods?.length) {
      return [...data.periods].sort((a, b) => a.period_no - b.period_no)
    }
    return Array.from({ length: 10 }, (_, i) => ({
      period_no: i + 1,
      label: `${i + 1}. Stunde`,
      start_time: null,
      end_time: null
    }))
  }, [data?.periods])

  const { todayWd, currentWeekday, currentPeriodNo, nextWeekday, nextPeriodNo, todayInGrid } = useMemo(() => {
    const now = new Date()
    const tw = untisWeekdayFromDate(now)
    const h = computeTimetableHighlights(now, tw, weekdays, periodRows, cellMap)
    return {
      todayWd: tw,
      currentWeekday: h.currentWeekday,
      currentPeriodNo: h.currentPeriodNo,
      nextWeekday: h.nextWeekday,
      nextPeriodNo: h.nextPeriodNo,
      todayInGrid: h.todayInGrid
    }
  }, [nowTick, weekdays, periodRows, cellMap])

  const errMsg = error instanceof Error ? error.message : String(error || '')

  return (
    <Tile>
      <TileContent className="space-y-2 py-2">
        {allowUserPicker && (teachersRes?.length || 0) > 0 && (
          <div className="flex w-full max-w-xs flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Lehrkraft (nur Admin/Schulleitung)
            </label>
            <select
              className="input-field w-full max-w-xs text-sm"
              aria-label="Lehrkraft für Stundenplan"
              value={selectedUserId === '' ? '' : String(selectedUserId)}
              onChange={(ev) => {
                const v = ev.target.value
                if (!v) {
                  setSelectedUserId('')
                  return
                }
                setSelectedUserId(Number(v))
              }}
            >
              <option value="">Eigener Account</option>
              {(teachersRes || []).map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {getTeacherLabel(t)}
                </option>
              ))}
            </select>
          </div>
        )}

        {isLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Stundenplan wird geladen…</p>}
        {isError && (
          <p className="text-sm text-red-600 dark:text-red-400">{errMsg || 'Laden fehlgeschlagen'}</p>
        )}

        {!isLoading && !data?.timetable && (
          <EmptyState
            title="Kein aktiver Stundenplan"
            description="Importieren Sie unter Stundenpläne einen Plan und aktivieren Sie ihn, damit er hier erscheint."
          />
        )}

        {!isLoading && data?.timetable && (
          <>
            <TableScrollArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell className="w-36">Stunde</TableHeaderCell>
                    {weekdays.map((wd) => (
                      <TableHeaderCell
                        key={wd}
                        className={
                          wd === todayWd && todayInGrid
                            ? 'border-x border-primary-200 bg-primary-50/50 dark:border-primary-800/60 dark:bg-primary-950/35'
                            : wd === nextWeekday && nextPeriodNo != null
                              ? 'border-x border-amber-200/80 bg-amber-50/40 dark:border-amber-800/50 dark:bg-amber-950/25'
                              : undefined
                        }
                      >
                        {data.weekday_labels?.[wd] || `WT${wd}`}
                        {wd === todayWd && todayInGrid ? <span className="sr-only"> (heute)</span> : null}
                        {wd === nextWeekday && nextPeriodNo != null && wd !== todayWd ? (
                          <span className="sr-only"> (nächste Unterrichtsstunde)</span>
                        ) : null}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodRows.length === 0 ? (
                    <EmptyTableRow colSpan={1 + weekdays.length} title="Keine Perioden definiert" />
                  ) : (
                    periodRows.map((p) => {
                      return (
                        <TableRow key={p.period_no}>
                          <TableCell className="whitespace-normal text-sm font-medium text-gray-800 dark:text-gray-200">
                            {formatPeriodLabel(p)}
                          </TableCell>
                          {weekdays.map((wd) => {
                            const list = cellMap.get(`${wd}:${p.period_no}`) || []
                            const hl = getLessonHighlightType(
                              wd,
                              p.period_no,
                              todayWd,
                              todayInGrid,
                              currentWeekday,
                              currentPeriodNo,
                              nextWeekday,
                              nextPeriodNo
                            )
                            return (
                              <TableCell
                                key={`${wd}-${p.period_no}`}
                                className="relative align-top whitespace-normal min-w-[7rem]"
                                title={
                                  hl === 'current'
                                    ? 'Aktuelle Stunde'
                                    : hl === 'next'
                                      ? 'Nächste Unterrichtsstunde'
                                      : undefined
                                }
                              >
                                {hl === 'current' ? (
                                  <span className="lr-timetable-cell-highlight lr-timetable-cell-highlight--current" aria-hidden />
                                ) : null}
                                {hl === 'next' ? (
                                  <span className="lr-timetable-cell-highlight lr-timetable-cell-highlight--next" aria-hidden />
                                ) : null}
                                <div className="relative z-0">{formatCell(list, 'teacher', subjectColorById)}</div>
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableScrollArea>
            {(currentPeriodNo != null || nextPeriodNo != null) && (
              <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="lr-timetable-legend-swatch lr-timetable-legend-swatch--current" aria-hidden />
                  Laufende Stunde
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="lr-timetable-legend-swatch lr-timetable-legend-swatch--next" aria-hidden />
                  Nächste Unterrichtsstunde
                </span>
              </p>
            )}
          </>
        )}

        {!isLoading && data?.timetable && (!data.entries || data.entries.length === 0) && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Keine Stunden für diese Lehrkraft im aktiven Plan (UNTIS-Zuordnung zu Benutzer prüfen).
          </p>
        )}
      </TileContent>
    </Tile>
  )
}
